const CURRENCY = 'IDR';
const PENDING_PURCHASE_KEY = 'meta_pixel_pending_purchase';
const PURCHASED_KEY_PREFIX = 'meta_pixel_purchased_';

const canTrack = () =>
    typeof window !== 'undefined' && typeof window.fbq === 'function';

const toNumber = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : 0;
};

const track = (eventName, params) => {
    if (!canTrack()) {
        return;
    }
    if (params) {
        window.fbq('track', eventName, params);
    } else {
        window.fbq('track', eventName);
    }
};

export const trackPageView = () => {
    track('PageView');
};

export const trackViewContent = ({id, name, value}) => {
    if (emptyId(id)) {
        return;
    }
    track('ViewContent', {
        content_ids: [String(id)],
        content_name: name,
        content_type: 'product',
        value: toNumber(value),
        currency: CURRENCY,
    });
};

export const trackAddToCart = ({id, name, value, quantity = 1}) => {
    if (emptyId(id)) {
        return;
    }
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const unitValue = toNumber(value);
    track('AddToCart', {
        content_ids: [String(id)],
        content_name: name,
        content_type: 'product',
        contents: [{id: String(id), quantity: qty}],
        value: unitValue * qty,
        currency: CURRENCY,
    });
};

export const buildCartPixelPayload = (cartItems = []) => {
    const contents = [];
    const content_ids = [];
    let value = 0;
    let num_items = 0;

    (cartItems || []).forEach((item) => {
        const id = item.product_detail_id || item.product_detail?.id || item.id;
        if (emptyId(id)) {
            return;
        }
        const qty = Math.max(1, parseInt(item.qty, 10) || 1);
        const unitPrice = toNumber(
            item.product_detail?.net_price ?? item.net_price ?? item.grandPrice ?? 0
        );
        content_ids.push(String(id));
        contents.push({id: String(id), quantity: qty});
        value += unitPrice * qty;
        num_items += qty;
    });

    return {
        content_ids,
        contents,
        content_type: 'product',
        value,
        num_items,
        currency: CURRENCY,
    };
};

export const trackInitiateCheckout = (cartItems = []) => {
    const payload = buildCartPixelPayload(cartItems);
    if (!payload.content_ids.length) {
        return;
    }
    track('InitiateCheckout', payload);
};

export const savePendingPurchase = (payload) => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
    }
    try {
        window.sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(payload));
    } catch (e) {
        // ignore storage failures
    }
};

export const consumePendingPurchase = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return null;
    }
    try {
        const raw = window.sessionStorage.getItem(PENDING_PURCHASE_KEY);
        if (!raw) {
            return null;
        }
        window.sessionStorage.removeItem(PENDING_PURCHASE_KEY);
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

export const trackPurchase = ({value, content_ids, contents, num_items, eventId}) => {
    if (eventId && hasTrackedPurchase(eventId)) {
        return;
    }

    const params = {
        content_type: 'product',
        currency: CURRENCY,
        value: toNumber(value),
    };

    if (content_ids && content_ids.length) {
        params.content_ids = content_ids.map(String);
    }
    if (contents && contents.length) {
        params.contents = contents;
    }
    if (num_items) {
        params.num_items = num_items;
    }

    track('Purchase', params);

    if (eventId) {
        markPurchaseTracked(eventId);
    }
};

const emptyId = (id) => id === null || id === undefined || id === '';

const hasTrackedPurchase = (eventId) => {
    try {
        return window.sessionStorage.getItem(PURCHASED_KEY_PREFIX + eventId) === '1';
    } catch (e) {
        return false;
    }
};

const markPurchaseTracked = (eventId) => {
    try {
        window.sessionStorage.setItem(PURCHASED_KEY_PREFIX + eventId, '1');
    } catch (e) {
        // ignore
    }
};
